import { Source, Layer } from 'react-map-gl';
import { COLOR } from '../styles';
import type { CapaEstilo, CapaRender } from '../types';

/**
 * DynamicCapaLayer — render presentacional de una capa informativa (overlay de
 * referencia). Una capa puede MEZCLAR geometrías (líneas + puntos + polígonos),
 * así que monta 3 sub-`<Layer>` filtrados por `geometry-type`:
 *   - fill   → polígonos (relleno tenue)
 *   - line   → polígonos (contorno) + líneas
 *   - circle → puntos
 * Los ids derivan del capa_id (`capa-<id>-{fill,line,circle}`) para que el
 * canal de interacción / toggles del MapaParcelas los enganche.
 *
 * Estilo validado con defaults seguros: un color/anchura inválido haría que
 * Mapbox rechace el paint en silencio.
 */

const DEFAULT_COLOR = COLOR.amber; // #FBBF24 — distinto de parcelas/macroalineamiento

interface ResolvedEstilo {
  color: string;
  grosor: number;
  opacidad: number;
  relleno: boolean;
}

export function resolveCapaEstilo(e?: CapaEstilo): ResolvedEstilo {
  const color =
    typeof e?.color === 'string' && /^(#|rgb)/i.test(e.color.trim()) ? e.color.trim() : DEFAULT_COLOR;
  const grosor = typeof e?.grosor === 'number' && e.grosor > 0 && e.grosor <= 20 ? e.grosor : 2;
  const opacidad = typeof e?.opacidad === 'number' && e.opacidad >= 0 && e.opacidad <= 1 ? e.opacidad : 0.6;
  const relleno = e?.relleno !== false;
  return { color, grosor, opacidad, relleno };
}

export interface DynamicCapaLayerProps {
  capa: CapaRender;
  /** Multiplicador global de opacidad (slider del toolbar). Default 1. */
  opacityMul?: number;
}

export function DynamicCapaLayer({ capa, opacityMul = 1 }: DynamicCapaLayerProps) {
  const sid = `capa-${capa.id}`;
  const s = resolveCapaEstilo(capa.estilo);
  const op = Math.max(0, Math.min(1, opacityMul));

  return (
    <Source id={sid} type="geojson" data={capa.data}>
      {/* Relleno de polígonos (tenue para no tapar lo de abajo). */}
      <Layer
        id={`${sid}-fill`}
        type="fill"
        filter={['==', ['geometry-type'], 'Polygon']}
        paint={{
          'fill-color': s.color,
          'fill-opacity': (s.relleno ? s.opacidad * 0.35 : 0) * op,
        }}
      />
      {/* Contorno de polígonos + líneas. */}
      <Layer
        id={`${sid}-line`}
        type="line"
        filter={['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'LineString']]}
        layout={{ 'line-cap': 'round', 'line-join': 'round' }}
        paint={{
          'line-color': s.color,
          'line-width': s.grosor,
          'line-opacity': s.opacidad * op,
        }}
      />
      {/* Puntos. */}
      <Layer
        id={`${sid}-circle`}
        type="circle"
        filter={['==', ['geometry-type'], 'Point']}
        paint={{
          'circle-color': s.color,
          'circle-radius': s.grosor + 2,
          'circle-opacity': s.opacidad * op,
          'circle-stroke-width': 1,
          'circle-stroke-color': 'rgba(255,255,255,0.85)',
        }}
      />
    </Source>
  );
}
